import { useState, useRef, Children } from 'react';

import { Box, useMediaQuery, useTheme } from '@mui/material';

import { Maybe } from '@/types';
import { Asset, FocalPointImage as TFocalPointImage, Video } from '@/types/generated';
import ImageWrapper from '@/components/ImageWrapper';
import { resolveContentfulAspectRatio } from '@/utils/resolveContentfulAspectRatio';

import YouTubeVideoEmbed from '../YouTubeVideoEmbed';
import AspectRatioPaddingBox from '../AspectRatioPaddingBox';
import { getImageWrapperStyles } from '../Service/ServiceMedia/ServiceMedia.styles';

import { getVideoStyles, youtubeVideoStyles, posterImageStyles, videoPlayerContentStyles } from './VideoContent.styles';
import { useHlsStream, useVideoContentPlayback } from './VideoContent.hooks';
import VideoContentControls from './VideoContentControls';

interface IVideoContent {
	posterImage?: Maybe<Asset>;
	focalPointPosterImage?: Maybe<TFocalPointImage>;
	video?: Maybe<Asset>;
	videoElSx?: React.CSSProperties;
	videoControlsSx?: React.CSSProperties;
	controllableVideo?: Maybe<Video>;
	children?: React.ReactNode;
	dataTestId?: string;
	richTextStylePosition?: string;
	loadVideoInPreviewMode?: boolean;
	fromHero?: boolean;
	sixteenByNineVideoRatio?: boolean;
	isNoHeroContent?: boolean;
	mediaAspectRatioSmallScreen?: Maybe<string>;
	overrodeAutoplay?: boolean;
}

export default function VideoContent({
	video,
	controllableVideo,
	posterImage,
	videoElSx = {},
	children,
	dataTestId = 'video_content',
	richTextStylePosition,
	videoControlsSx = {},
	loadVideoInPreviewMode = false,
	fromHero = false,
	sixteenByNineVideoRatio = false,
	isNoHeroContent = false,
	mediaAspectRatioSmallScreen,
	overrodeAutoplay,
}: Readonly<IVideoContent>) {
	const {
		autoPlay = false,
		isLooped = true,
		isMuted = true,
		media,
		posterMedia,
		posterUrl,
		youtubeId,
		useOriginalVideoSize = false,
		videoSource,
		hlsVideoStreamLink,
	} = Object(controllableVideo) as Video;
	const computedPosterUrl = posterUrl ?? posterMedia?.url ?? posterImage?.url ?? '';
	const computedAutoplay = Boolean(autoPlay || overrodeAutoplay);
	const videoRef = useRef<HTMLVideoElement | null>(null);
	const [isNowMuted, setIsNowMuted] = useState<boolean | null>(computedAutoplay ? true : isMuted);
	const [isVideoHovered, setIsVideoHovered] = useState<boolean>(false);
	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
	const isExtraSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
	const isPortraitVideo = resolveContentfulAspectRatio(mediaAspectRatioSmallScreen) === '9:16';

	const {
		isAudioControlsVisible,
		isFirstPlayTriggered,
		isLongVideo,
		isPlaying,
		isPosterVisual,
		isVideoReady,
		setIsVideoReady,
		togglePlayback,
	} = useVideoContentPlayback(videoRef, {
		autoPlay: computedAutoplay,
		hasPoster: !!computedPosterUrl,
		isLooped: !!isLooped,
		loadVideoInPreviewMode,
		youtubeId,
	});

	useHlsStream(videoRef, videoSource, hlsVideoStreamLink);

	const imageWrapperSx = getImageWrapperStyles(true);

	const videoUrl = media?.url ?? video?.url ?? '';

	function handleMuteUnmute() {
		const videoEl = videoRef.current;

		if (!videoEl) {
			return;
		}

		videoEl.muted = !isNowMuted;
		setIsNowMuted(videoEl.muted);
	}

	let hasSourceElements = false;
	Children.forEach(children, (c) => {
		if ((c as React.ReactElement)?.type === 'source') {
			hasSourceElements = true;
		}
	});

	const videoSrc = hasSourceElements ? undefined : videoUrl || undefined;
	const isHoveredStylesShowed = isVideoHovered || (!isFirstPlayTriggered && !computedAutoplay) || !isPlaying;

	if (videoSource === 'Youtube' || youtubeId?.trim()) {
		if (!youtubeId?.trim()) return null;

		const smallScreenAspectRatio = sixteenByNineVideoRatio ? '16:9' : '1:1';

		return (
			<AspectRatioPaddingBox
				aspectRatio={isSmallScreen ? smallScreenAspectRatio : '16:9'}
				placeholderColor="transparent"
				imageWrapperSx={imageWrapperSx}
				paddingBoxSx={youtubeVideoStyles(richTextStylePosition)}
			>
				<YouTubeVideoEmbed
					youTubeVideoId={youtubeId}
					isSmallScreen={isSmallScreen}
					isBorderRadius={false}
					youTubeVideoImage={posterMedia}
					autoPlay={computedAutoplay}
					isLooped={isLooped}
					isMuted={isMuted}
					fromHero={fromHero}
					videoControlsSx={videoControlsSx}
					sixteenByNineVideoRatio={sixteenByNineVideoRatio}
				/>
			</AspectRatioPaddingBox>
		);
	}

	return (
		<Box
			sx={videoPlayerContentStyles}
			onMouseOver={() => setIsVideoHovered(true)}
			onMouseOut={() => setIsVideoHovered(false)}
			onTouchStart={() => {
				setIsVideoHovered(true);
				setTimeout(() => {
					setIsVideoHovered(false);
				}, 1500);
			}}
		>
			{isPosterVisual && (
				<ImageWrapper
					src={computedPosterUrl}
					data-testid="img_video-poster"
					alt="img_video-poster"
					sx={posterImageStyles}
				/>
			)}
			<video
				playsInline
				ref={videoRef}
				controls={false}
				muted={!!isNowMuted}
				loop={!!isLooped}
				autoPlay={computedAutoplay}
				style={{ ...videoElSx, ...getVideoStyles(useOriginalVideoSize, isNoHeroContent, fromHero) }}
				disablePictureInPicture
				data-testid={dataTestId}
				src={videoSrc}
				onCanPlayThrough={() => setIsVideoReady(true)}
				id={controllableVideo?.sys?.id ?? ''}
				aria-label={media?.description ?? ''}
			>
				{children}
			</video>

			{isVideoReady && !useOriginalVideoSize && isLongVideo ? (
				<VideoContentControls
					dataTestId={`group_video_controls_for_${controllableVideo?.sys?.id ?? ''}`}
					isAudioControlsVisible={isAudioControlsVisible}
					isExtraSmallScreen={isExtraSmallScreen}
					isHoveredStylesShowed={isHoveredStylesShowed}
					isMuted={!!isNowMuted}
					isPlaying={isPlaying}
					isPortraitVideo={isPortraitVideo}
					isSmallScreen={isSmallScreen}
					onToggleMute={handleMuteUnmute}
					onTogglePlayback={togglePlayback}
					richTextStylePosition={richTextStylePosition}
					videoControlsSx={videoControlsSx}
				/>
			) : null}
		</Box>
	);
}
